import type { ReactNode } from 'react'
import { Button, ProgressBar } from '@systemeio/components'
import { ArrowLeftIcon, File05Icon, GraduationHat01Icon } from '@systemeio/icons'
import { useTranslation } from 'next-i18next/pages'
import Image from 'next/image'
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch'
import { useFormatDateTime } from 'shared/hooks/use-format-date-time'
import useUser, { isAuthorizedUser } from 'shared/hooks/use-user'
import { SchoolCourseEnrollmentsDataInterface } from 'modules/school/types/school-enrollments-interface'
import { getCourseCardLinkTarget } from 'modules/school/utils/get-course-card-link-target'

export interface CourseCardProps {
  data: SchoolCourseEnrollmentsDataInterface
}

export const CourseCard = ({ data }: CourseCardProps): ReactNode => {
  const { t } = useTranslation()

  const { user } = useUser()
  const accessFromFormatted = useFormatDateTime(data.accessFrom)
  const button = !data.accessFrom && (
    <Button
      variant="primary"
      className="mt-3 w-full min-w-30 self-start sm:mt-0 sm:w-[unset]"
      render={
        <LinkWithoutPrefetch
          href={data.resumeUrl}
          target={getCourseCardLinkTarget(data.resumeUrl)}
        />
      }
    >
      {!data.progress ? t('school.card.button.open_course') : t('school.card.button.resume_course')}
      <ArrowLeftIcon className="size-5 rotate-180" />
    </Button>
  )

  return (
    <div className="flex flex-col rounded-2xl md:flex-row">
      <div className="w-full md:w-[550px]">
        <div className="relative aspect-video w-full overflow-hidden rounded-lg">
          <div className="absolute inset-0 size-full">
            {data.image?.path ? (
              <Image
                data-testid="course-card-image"
                className="block size-full object-cover object-center"
                src={data.image?.path}
                alt={`Course image - ${data.name}`}
                width={864}
                height={480}
                unoptimized
              />
            ) : (
              <div
                data-testid="course-card-image"
                className="flex size-full items-center justify-center bg-bg-accent-active"
              >
                <GraduationHat01Icon className="size-1/2 text-text-on" />
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="mt-4 flex basis-full flex-col md:mt-0 md:pl-6">
        <div>
          <div className="flex flex-col-reverse justify-between gap-4 sm:flex-row">
            <div>
              <h5 className="text-lg font-bold text-text-primary sm:text-xl">{data.name}</h5>
              {!!data.instructorName && (
                <p className="mt-2 text-base font-normal text-text-secondary">
                  {data.instructorName}
                </p>
              )}
            </div>

            <div className="hidden sm:block">{button}</div>
          </div>
          {data.description && (
            <p
              className="mt-2 max-h-28 overflow-y-auto text-base wrap-break-word whitespace-pre-line"
              dangerouslySetInnerHTML={{ __html: data.description }}
            />
          )}
        </div>

        <div className="mt-auto flex flex-wrap justify-between gap-2 sm:flex-nowrap">
          {data.accessFrom && isAuthorizedUser(user) ? (
            <p className="mt-2 text-sm text-text-primary/70">
              {t('school.card.waiting_for_access', {
                date: accessFromFormatted?.formatted,
              })}
            </p>
          ) : (
            <ProgressBar
              value={data.progress}
              valueLabel={`${data.progress}%`}
              rootProps={{
                'data-test-id': 'course-progress-bar',
                className: 'mt-3 w-full min-w-0 sm:flex-1',
              }}
            />
          )}
          <div className="flex justify-end sm:mt-3 sm:basis-50">
            <span className="flex items-center gap-1 text-sm text-text-tertiary sm:text-base">
              <File05Icon className="size-5" />
              {t('school.card.lessons_counter', {
                progress: `${!data.accessFrom ? `${data.completedLecturesCount}/` : ''}${
                  data.totalLecturesCount
                }`,
              })}
            </span>
          </div>
        </div>
        {<div className="sm:hidden">{button}</div>}
      </div>
    </div>
  )
}
